import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    computed,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { differenceInCalendarDays, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { catchError, of, switchMap } from 'rxjs';

import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Tooltip } from 'primeng/tooltip';

import { ProductTaxRateApiService } from '@bcmApiServices/product-tax-rate.api-service';
import { BcmService } from '@modules/bcm/bcm.service';
import { TranslationModule } from '@core/translation/translation.module';
import { ITaxRate } from '@shared/models/tax-rate';
import { U2bDatePickerButtonBarComponent } from '@u2bUi/date-picker/u2b-date-picker-button-bar.component';
import { U2B_DATE_PICKER_CUSTOM_TIME_PT } from '@u2bUi/date-picker/u2b-date-picker.pt';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bDatePickerApplyDirective } from '@u2bUi/directives/u2b-date-picker-apply.directive';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';

import {
    applyReservationDefaultTimes,
    applyReservationValidUntilDefaultTime,
    isMidnight,
    toReservationCheckInDefaultTime,
    toReservationValidUntilDefaultTime,
} from '@shared/functions/apply-reservation-default-times';

import { BookingV2DateRange, BookingV2Facade } from '../../services/booking-v2.facade';

interface DateRangeForm {
    from: FormControl<Date | null>;
    to: FormControl<Date | null>;
    openEnd: FormControl<boolean>;
}

@Component({
    selector: 'booking-v2-date-range-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        DatePicker,
        InputNumber,
        Select,
        Textarea,
        ToggleSwitch,
        Tooltip,
        TranslationModule,
        U2bDateMaskDirective,
        U2bDatePickerApplyDirective,
        U2bDatePickerButtonBarComponent,
        U2bIndicatorCardComponent,
    ],
    templateUrl: './date-range-step.component.html',
    host: { class: 'block' },
    // Recolor only the disabled "open end" datepicker to the primary accent.
    // PrimeNG reads these vars from the surrounding scope; setting them on the
    // field's host cascades into its input + dropdown (no ::ng-deep needed).
    styles: [`
        .u2b-open-end-field {
            --p-inputtext-border-color: var(--p-primary-color);
            --p-inputtext-placeholder-color: var(--p-primary-color);
            --p-inputtext-disabled-color: var(--p-primary-color);
            --p-inputtext-disabled-background: color-mix(in srgb, var(--p-primary-color) 8%, transparent);
            --p-datepicker-dropdown-border-color: var(--p-primary-color);
            --p-datepicker-dropdown-color: var(--p-primary-color);
        }
    `],
})
export class DateRangeStepComponent implements OnInit {

    private readonly fb = inject(FormBuilder);
    readonly facade = inject(BookingV2Facade);
    private readonly destroyRef = inject(DestroyRef);
    private readonly taxRateApi = inject(ProductTaxRateApiService);

    readonly currentLocale = BcmService.currentLocale;
    readonly maxNoteLength = 512;

    /** Hides PrimeNG's read-only clock; the button bar renders a typable one. */
    readonly customTimePt = U2B_DATE_PICKER_CUSTOM_TIME_PT;

    // Reservation mode shows the confirm-by deadline + notes; the deposit card is
    // create-only (on edit the deposit already lives as a saved position).
    readonly isReservation = computed(() => this.facade.bookingType() === 'reservation');
    readonly showDeposit = computed(() => this.isReservation() && !this.facade.isEditMode());

    // Times of day a typed date falls back to while the field is empty — otherwise a bare date
    // would land on midnight. reapplyDefaultTimes() still refines these for blank-open bookings.
    readonly checkInFallbackTime = computed(
        () => toReservationCheckInDefaultTime(this.facade.defaultTimeUnit()));
    readonly checkOutFallbackTime = computed(
        () => toReservationValidUntilDefaultTime(this.facade.defaultTimeUnit()));
    readonly reservationValidUntilFallbackTime = this.checkOutFallbackTime;

    // Reservation-context wording swaps the header copy of the (shared) step.
    readonly titleKey = computed(() => this.isReservation()
        ? 'bookingV2.reservation.dateRange.title'
        : 'bookingV2.dateRange.title');
    readonly subtitleKey = computed(() => this.isReservation()
        ? 'bookingV2.reservation.dateRange.subtitle'
        : 'bookingV2.dateRange.subtitle');

    // Tax rates for the deposit position — only fetched once the deposit card is
    // actually shown (no extra request for plain bookings).
    readonly taxRates = toSignal(
        toObservable(this.showDeposit).pipe(
            switchMap(show => show
                ? this.taxRateApi.getAll().pipe(catchError(() => of<ITaxRate[]>([])))
                : of<ITaxRate[]>([])),
        ),
        { initialValue: [] as ITaxRate[] },
    );

    readonly form = this.fb.group<DateRangeForm>({
        from: new FormControl<Date | null>(null, Validators.required),
        to: new FormControl<Date | null>(null, Validators.required),
        openEnd: new FormControl<boolean>(false, { nonNullable: true }),
    });

    readonly openEnd = signal<boolean>(false);
    readonly fromValue = signal<Date | null>(null);

    // Default-time management. Only blank-opened bookings auto-manage times; on an
    // edit or an entry point that prefilled dates, the times are the user's/context's
    // and must never be re-defaulted, so `autoContext` stays false there.
    private autoContext = false;
    // Set once the user takes manual control of a field's time (typed/spun), after
    // which that field is never re-defaulted.
    private fromManual = false;
    private toManual = false;
    // Last observed form values, used to tell a manual time edit (our own patches are
    // silent) from a plain date pick.
    private prevFrom: Date | null = null;
    private prevTo: Date | null = null;
    // Stable reference for the picker's `defaultDate` — see reservationValidUntilDefaultDate().
    private validUntilDefault: Date | null = null;

    readonly dateRange = this.facade.dateRange;

    // Nights between arrival and departure (only for a closed-end range). The unit
    // label follows the tenant's configured stay unit (facade.durationUnitLabelKey).
    readonly daysCount = computed(() => {
        const { from, to, openEnd } = this.dateRange();
        if (openEnd || !from || !to) {
            return 0;
        }
        return Math.max(differenceInCalendarDays(to, from), 0);
    });

    constructor() {
        // Preselect a sensible default tax rate for the deposit once rates load.
        effect(() => {
            const rates = this.taxRates();
            if (!this.facade.depositRequired() || !rates.length) {
                return;
            }
            untracked(() => {
                if (this.facade.depositTaxRateId() == null) {
                    // Prefer the 19% standard rate (the list is unordered), matching
                    // the services tab's default.
                    this.setDepositTaxRate((rates.find(t => t.value === 19) ?? rates[0]).id);
                }
            });
        });

        // Cold settings cache: a blank-open booking seeds its default check-in time
        // asynchronously, after this step's form has already mounted empty. Fill the
        // still-empty form once that late seed arrives (guarded so it never fights
        // user input — it only acts while `from` is empty).
        effect(() => {
            const dr = this.facade.dateRange();
            if (dr.from && !this.form.controls.from.value) {
                this.form.patchValue(
                    { from: dr.from, to: dr.to, openEnd: dr.openEnd },
                    { emitEvent: false },
                );
                this.openEnd.set(dr.openEnd);
                this.fromValue.set(dr.from);
                this.autoContext = this.facade.blankDefaultsApplied();
                this.fromManual = false;
                this.toManual = false;
                this.prevFrom = dr.from;
                this.prevTo = dr.to;
            }
        });
    }

    // Keep the id and its percentage in sync (the percentage drives the gross
    // deposit preview in the dispatch step).
    setDepositTaxRate(id: number | null): void {
        this.facade.depositTaxRateId.set(id);
        this.facade.depositVatValue.set(this.taxRates().find(t => t.id === id)?.value ?? 0);
    }

    ngOnInit(): void {
        const initial = this.facade.dateRange();
        this.form.patchValue({
            from: initial.from,
            to: initial.to,
            openEnd: initial.openEnd,
        }, { emitEvent: false });
        this.openEnd.set(initial.openEnd);
        this.fromValue.set(initial.from);
        // Only a blank-seeded range auto-manages times; edit/prefilled values are
        // the user's or the entry point's — mark them manual so they are preserved.
        this.autoContext = this.facade.blankDefaultsApplied();
        this.fromManual = !this.autoContext && initial.from != null;
        this.toManual = !this.autoContext && initial.to != null;
        this.prevFrom = initial.from;
        this.prevTo = initial.to;
        this.applyOpenEndState(initial.openEnd);

        this.form.controls.from.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(v => this.fromValue.set(v));

        this.form.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.reapplyDefaultTimes();
                this.pushToFacade();
            });
    }

    onOpenEndToggle(checked: boolean): void {
        this.openEnd.set(checked);
        this.applyOpenEndState(checked);
        if (checked) {
            this.form.controls.to.setValue(null, { emitEvent: false });
            // Re-enabling an end later should auto-fill the default check-out again.
            this.toManual = false;
            this.prevTo = null;
        }
        this.pushToFacade();
    }

    private timeKey(d: Date | null): number | null {
        return d ? d.getHours() * 60 + d.getMinutes() : null;
    }

    private sameDay(a: Date | null, b: Date | null): boolean {
        return !!a && !!b
            && a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();
    }

    // Keeps the check-in/out times aligned with the marina defaults as the range's
    // day-span flips (same-day ⇄ multi-day), without ever touching a field the user
    // set manually. Only runs for blank-opened bookings (`autoContext`); patches are
    // silent to avoid a feedback loop.
    private reapplyDefaultTimes(): void {
        const from = this.form.controls.from.value;
        const openEnd = this.form.controls.openEnd.value;
        const to = openEnd ? null : this.form.controls.to.value;

        // Our own patches are silent, so any observed time-of-day change is the user
        // spinning/typing a time — from then on that field is theirs.
        if (from && this.prevFrom && this.timeKey(from) !== this.timeKey(this.prevFrom)) {
            this.fromManual = true;
        }
        if (to && this.prevTo && this.timeKey(to) !== this.timeKey(this.prevTo)) {
            this.toManual = true;
        }

        const unit = this.facade.defaultTimeUnit();
        if (this.autoContext && unit && from) {
            const desired = applyReservationDefaultTimes(from, to, unit);
            const patch: { from?: Date; to?: Date | null } = {};

            if (!this.fromManual && this.timeKey(from) !== this.timeKey(desired.from)) {
                patch.from = desired.from;
            }
            // Re-apply the check-out when its time OR its day differs from the default
            // (the day differs when a same-day range is rolled to the next day for
            // marinas that forbid same-day bookings).
            if (to != null && !this.toManual && desired.to != null
                && (this.timeKey(to) !== this.timeKey(desired.to) || !this.sameDay(to, desired.to))) {
                patch.to = desired.to;
            }
            if (patch.from !== undefined || patch.to !== undefined) {
                this.form.patchValue(patch, { emitEvent: false });
            }
        }

        this.prevFrom = this.form.controls.from.value;
        this.prevTo = this.form.controls.to.value;
    }

    // Seeds the picker's clock while it has no value — without this PrimeNG starts the time at
    // "now", which has nothing to do with when the reservation should lapse. A plain method, not
    // computed(): computed() caches "now" from its first read and never advances on its own since
    // it has no tracked dependency that changes across midnight. The result is memoized by value
    // because PrimeNG's `defaultDate` setter re-runs initTime() for every changed binding, and
    // initTime() overwrites the panel's hour/minute unconditionally — a fresh Date instance per
    // change-detection cycle snapped the time spinner back on every click.
    reservationValidUntilDefaultDate(): Date {
        const next = applyReservationValidUntilDefaultTime(new Date(), this.facade.defaultTimeUnit());

        if (!this.validUntilDefault || this.validUntilDefault.getTime() !== next.getTime()) {
            this.validUntilDefault = next;
        }

        return this.validUntilDefault;
    }

    // Typing a bare date still yields midnight; a deadline of 00:00 would count as expired for the
    // whole of its last day, so a first-ever bare value falls back to the check-out time. Once a
    // deadline is already set, a midnight the user submits is deliberate (e.g. dialing the time
    // spinner down to 00:00) and must be kept, not silently overwritten on every re-pick.
    onReservationValidUntilChange(value: Date | null): void {
        const hadValue = this.facade.reservationValidUntil() !== null;

        this.facade.reservationValidUntil.set(
            value && isMidnight(value) && !hadValue
                ? applyReservationValidUntilDefaultTime(value, this.facade.defaultTimeUnit())
                : value,
        );
    }

    formatDate(date: Date | null): string {
        return date && !isNaN(date.getTime())
            ? format(date, "EEEE, dd. MMMM yyyy, HH:mm 'Uhr'", { locale: de })
            : '';
    }

    private applyOpenEndState(openEnd: boolean): void {
        if (openEnd) {
            this.form.controls.to.clearValidators();
        } else {
            this.form.controls.to.setValidators(Validators.required);
        }
        this.form.controls.to.updateValueAndValidity({ emitEvent: false });
    }

    private pushToFacade(): void {
        const v = this.form.getRawValue();
        this.facade.setDateRange({
            from: v.from ?? null,
            to: v.openEnd ? null : (v.to ?? null),
            openEnd: v.openEnd,
        });
    }
}
